import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';

import { getBaseURL } from '@/utils/environment';
import type { ChainAddress } from '@/utils/models';
import { routes } from '@/utils/routes';
import { resolveTradeRoute } from '@/utils/tradeRoutes';
import { tradeRoute, tradeRouteHref } from '@/utils/url';

type HomepageSeoProps = {
  // Set on the server so the image URL matches during hydration.
  ogTime: number;
};

export const HomepageSeo = ({ ogTime }: HomepageSeoProps) => {
  const { pathname, query } = useRouter();
  // Bare `/` keeps the generic metadata; trade routes describe their resolved chart token.
  const route = pathname === routes.INDEX ? undefined : resolveTradeRoute(pathname, query);
  const isTradeRoute = !!route;
  const chartToken = route?.chartToken;
  const { token } = useTokenResult(
    chartToken?.address as ChainAddress | undefined,
    chartToken?.chainId,
  );

  const seo = useMemo(() => {
    if (!token || !isTradeRoute) return undefined;

    const { name, symbol, address, chainId, chainName } = token;
    const baseUrl = getBaseURL();
    // Every trade type shares the token's `/swap` canonical so they index as one page.
    const url = `${baseUrl}${tradeRouteHref(tradeRoute({ address, chainId }))}`;
    const title = `Swap ${symbol} on ${chainName} | Trade at Best Price | Matcha`;
    const description = `Swap ${name} (${symbol}) on ${chainName} with Matcha — the DEX aggregator that finds you the best price across 100s of liquidity sources. No hidden fees. Connect your wallet and trade in seconds.`;
    const ogImageUrl = `${baseUrl}/api/token-og?chainId=${chainId}&address=${address}&time=${ogTime}`;

    return {
      title,
      description,
      canonical: url,
      openGraph: {
        title,
        description,
        url,
        images: [{ url: ogImageUrl, width: 1200, height: 630, alt: 'Matcha token cover image' }],
      },
      additionalMetaTags: [
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImageUrl },
        { name: 'twitter:url', content: url },
      ],
    };
  }, [token, isTradeRoute, ogTime]);

  if (!seo) return null;

  return <NextSeo {...seo} />;
};
